import BookingForm from "@/components/BookingForm";
import { Truck, Flower2, Users, Clock, PartyPopper, Building2, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";

export default function FloralBar() {
  return (
    <div className="page-transition pt-[72px]" data-testid="floral-bar-page">
      <Seo
        title="Mobile Floral Bar Experiences in the Twin Cities | Fleuris Noir"
        description="Book Fleuris Noir's mobile floral bar for events, activations and celebrations across Minneapolis and the Twin Cities — a luxury, hands-on floral experience."
        path="/floral-bar"
        jsonLd={{
          "@context": "https://schema.org", "@type": "Service",
          serviceType: "Mobile Floral Bar Experience",
          provider: { "@type": "Florist", name: "Fleuris Noir", telephone: "+1-612-547-9260", areaServed: "Minneapolis, Minnesota" },
          areaServed: "Twin Cities Metropolitan Area",
        }}
      />
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1760373071711-960143464e34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBmbG9yaXN0JTIwc2hvcHxlbnwwfHx8fDE3NzYyNjIwMzl8MA&ixlib=rb-4.1.0&q=85')` 
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        
        <div className="relative z-10 text-center px-6">
          <p className="overline mb-4">Mobile Pop-Up Experience</p>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#f8f5f0]">
            The Floral Bar
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="section bg-[#0c0c0c]">
        <div className="container-custom max-w-3xl text-center">
          <p className="font-body text-lg text-[#a19d98] leading-relaxed">
            The Floral Bar is our signature mobile pop-up experience. We bring 
            the beauty of floral design directly to your event — complete setup, 
            hands-on arrangement activity for your guests, and full teardown. 
            Perfect for private parties, corporate events, team-building, and celebrations.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="section bg-[#161616]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="overline mb-4">How It Works</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              We Come To You
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Truck className="text-[#b8966a]" size={28} />,
                title: "Full Setup & Teardown",
                desc: "We arrive at your venue, set up the entire floral bar station, and handle all cleanup after the event."
              },
              {
                icon: <Flower2 className="text-[#b8966a]" size={28} />,
                title: "Premium Blooms",
                desc: "We bring a curated selection of seasonal flowers, foliage, and all supplies for your guests."
              },
              {
                icon: <Users className="text-[#b8966a]" size={28} />,
                title: "Expert Guidance",
                desc: "Our florists guide each guest through creating their own unique arrangement to take home."
              },
              {
                icon: <Clock className="text-[#b8966a]" size={28} />,
                title: "Flexible Duration",
                desc: "Sessions tailored to your event schedule, typically 1.5 to 3 hours depending on group size."
              }
            ].map((item, i) => (
              <div key={i} className="text-center p-6 border border-[#b8966a]/10">
                <div className="w-14 h-14 mx-auto mb-4 border border-[#b8966a]/20 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-heading text-lg text-[#f8f5f0] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-[#a19d98]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Types */}
      <section className="section bg-[#0c0c0c] border-y border-[#b8966a]/10">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="overline mb-4">Perfect For</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              Book The Floral Bar For
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <PartyPopper className="text-[#b8966a]" size={28} />,
                title: "Private Parties",
                desc: "Birthdays, bridal showers, baby showers, girls' nights, and milestone celebrations. A unique activity your guests will love."
              },
              {
                icon: <Building2 className="text-[#b8966a]" size={28} />,
                title: "Corporate Events",
                desc: "Team-building workshops, client appreciation events, company retreats, and office parties. Elevate your brand experience."
              },
              {
                icon: <Sparkles className="text-[#b8966a]" size={28} />,
                title: "Special Occasions",
                desc: "Holiday gatherings, fundraisers, pop-up markets, and community events. We bring the beauty wherever you need it."
              }
            ].map((item, i) => (
              <div key={i} className="bg-[#161616] border border-[#b8966a]/10 p-8 text-center" data-testid={`floral-bar-event-${i}`}>
                <div className="w-14 h-14 mx-auto mb-4 border border-[#b8966a]/20 flex items-center justify-center">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl text-[#f8f5f0] mb-3">{item.title}</h3>
                <p className="font-body text-sm text-[#a19d98] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="section bg-[#161616]">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-12">
            <p className="overline mb-4">What's Included</p>
            <h2 className="font-heading text-3xl text-[#f8f5f0]">
              Every Floral Bar Booking Includes
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Full setup and teardown at your venue",
              "Curated selection of premium seasonal blooms",
              "All supplies — vases, tools, wrapping",
              "On-site florist to guide guests",
              "Each guest takes home their arrangement",
              "Customizable to match your event theme",
              "Flexible timing to fit your schedule",
              "Serves groups of any size"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#0c0c0c] border border-[#b8966a]/10">
                <div className="w-1.5 h-1.5 bg-[#b8966a] rounded-full mt-2 flex-shrink-0" />
                <p className="font-body text-[#f8f5f0]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="section bg-[#0c0c0c] border-y border-[#b8966a]/10">
        <div className="container-custom text-center max-w-2xl">
          <p className="overline mb-4">Pricing</p>
          <h2 className="font-heading text-3xl text-[#f8f5f0] mb-6">
            Custom Quotes
          </h2>
          <p className="font-body text-[#a19d98] mb-4 leading-relaxed">
            Every event is unique. Pricing is based on group size, location, 
            duration, and your preferred bloom selection. Reach out and we'll 
            put together a custom quote for your event.
          </p>
          <p className="font-body text-sm text-[#b8966a]">
            We serve the greater Minneapolis area and beyond.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="section bg-[#161616]">
        <div className="container-custom max-w-4xl">
          <BookingForm 
            bookingType="floral_bar" 
            title="Book The Floral Bar For Your Event" 
          />
        </div>
      </section>
    </div>
  );
}
